import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {ControlledAccordion} from '@Components/controlled-accordion';
import {Text, TextSize} from '@Components/text';
import styles from './download-more-options.module.scss';
import {IconSize} from '@Components/icon-v2/icon.types';
import {DownloadBleedOption} from '@Components/download-panel/components/download-bleed-option/download-bleed-option';
import {DownloadSpeedOption} from '@Components/download-panel/components/download-speed-option/download-speed-option';
import {DownloadMarginOption} from '@Components/download-panel/components/download-margin-option/download-margin-option';
import {DownloadAdjustLetterSizeOption} from '@Components/download-panel/components/download-adjust-letter-size-option/download-adjust-letter-size-option';
import {noop} from '@Utils/general.util';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {FileTypeOptions} from '@Components/download-panel/components/file-type-option/file-type-option.types';
import {DownloadVideoDurationOption} from '@Components/download-panel/components/download-video-duration-option/download-video-duration-option';
import {DownloadVideoDurationInformation} from '@Components/download-panel/components/download-video-duration-information/download-video-duration-information';
import {isDurationEnabledForGif} from '@Libraries/poster-download-library';
import {DownloadGifDurationOption} from '@Components/download-panel/components/download-gif-duration-option/download-gif-duration-option';
import {DOWNLOAD_GIF_MAX_DURATION} from '@Components/download-panel/components/download-gif-duration-option/download-gif-duration-option.types';

interface DownloadMoreOptionsProps {
  onToggleOpenClose?: () => void;
  onSlideCallback?(): void;
  onSlideEndedCallback?(): void;
}

export function DownloadMoreOptions({onToggleOpenClose = noop, onSlideCallback = noop, onSlideEndedCallback = noop}: DownloadMoreOptionsProps): ReactElement | null {
  const {fileType, isWebDownload, setDuration, duration, originalDuration, posterObject} = useDownloadPanelData();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (fileType === FileTypeOptions.GIF && originalDuration > DOWNLOAD_GIF_MAX_DURATION) {
      setDuration(DOWNLOAD_GIF_MAX_DURATION);
    }

    if (fileType === FileTypeOptions.MP4 && duration !== originalDuration) {
      setDuration(originalDuration);
    }
  }, [fileType]);

  if (fileType === FileTypeOptions.WEBP || fileType === FileTypeOptions.WEBM) {
    return null;
  }

  if (fileType === FileTypeOptions.PNG || fileType === FileTypeOptions.JPG) {
    if (isWebDownload) {
      return null;
    }
  }

  if (fileType === FileTypeOptions.GIF && !isDurationEnabledForGif(posterObject)) {
    return null;
  }
  const getHeader = (): ReactElement => {
    return (
      <div>
        <Text className="spacing-m-r-1" bold size={TextSize.XSMALL} val={window.i18next.t('pmwjs_more_options')} />
      </div>
    );
  };

  const onHeaderClick = (): void => {
    onToggleOpenClose();
    setIsOpen(!isOpen);
  };

  const getMoreOptionsAccordingToFileType = (): ReactElement | null => {
    switch (fileType) {
      case FileTypeOptions.PDF:
        return (
          <>
            <DownloadBleedOption onSlideCallback={onSlideCallback} onSlideEndedCallback={onSlideEndedCallback} />
            <DownloadMarginOption />
            <DownloadAdjustLetterSizeOption />
          </>
        );
      case FileTypeOptions.MP4:
        return (
          <>
            <DownloadVideoDurationOption onSlideCallback={onSlideCallback} onSlideEndedCallback={onSlideEndedCallback} />
            <DownloadSpeedOption onSlideCallback={onSlideCallback} onSlideEndedCallback={onSlideEndedCallback} />
            <DownloadVideoDurationInformation />
          </>
        );
      case FileTypeOptions.GIF:
        return (
          <>
            <DownloadGifDurationOption onSlideCallback={onSlideCallback} onSlideEndedCallback={onSlideEndedCallback} />
            <DownloadSpeedOption onSlideCallback={onSlideCallback} onSlideEndedCallback={onSlideEndedCallback} />
            <DownloadVideoDurationInformation />
          </>
        );
      case FileTypeOptions.PNG:
      case FileTypeOptions.JPG:
        if (!isWebDownload) {
          return <DownloadBleedOption onSlideCallback={onSlideCallback} onSlideEndedCallback={onSlideEndedCallback} />;
        }
        return null;
      default:
        return null;
    }
  };

  return (
    <ControlledAccordion
      iconSize={IconSize.SIZE_ICON_20}
      headerClassName={`${styles.header} ${isOpen ? styles.isOpen : ''}`}
      onHeaderClick={onHeaderClick}
      header={getHeader()}
      isOpen={isOpen}
    >
      <div className={`spacing-p-t-4 spacing-p-r-4 spacing-p-b-3 spacing-p-l-4 flex flex-v-row ${styles.gap}`}>{getMoreOptionsAccordingToFileType()}</div>
    </ControlledAccordion>
  );
}
